// src/components/EmailDraftModal.jsx
// ============================================================================
// Reusable email draft modal for timeline-triggered client notifications.
// Supports: addressee field, intro/closing sections, template categories,
// validation (recipient, addressee, PDF attachment), CC/BCC, communication log.
// ============================================================================
import React, { useState, useMemo, useCallback } from 'react';
import { Mail, X, UserPlus, ChevronDown, ChevronUp, AlertTriangle, FileText, RotateCcw } from 'lucide-react';
import { BrioModal, BrioActionGroup } from './BrioComponents';
import {
  renderEmailTemplate,
  getClientContactsForEvent,
  getAllClientContacts,
  isValidEmail,
  buildMailtoUrl,
  formatDateForEmail,
  isFinanceEvent,
  getAdminBccEmail,
  buildTemplateVars,
  validateEmailDraft,
  requiresInvoicePdf,
} from '../utils/emailTemplates';

// ── Styles (aligned with BRIO design system) ──────────────────────────────
const INPUT_STYLE = {
  width: '100%',
  padding: '6px 10px',
  border: '1px solid var(--brio-border, #d1d5db)',
  borderRadius: '6px',
  fontSize: '13px',
  lineHeight: '1.4',
  background: '#fff',
  color: 'var(--brio-text, #1e293b)',
  boxSizing: 'border-box',
};
const TEXTAREA_STYLE = { ...INPUT_STYLE, minHeight: '160px', resize: 'vertical', fontFamily: "'Raleway', sans-serif" };
const LABEL_STYLE = { fontSize: '12px', fontWeight: 600, color: 'var(--brio-text, #475569)', marginBottom: '4px', display: 'block' };
const CHIP_STYLE = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '3px 10px', borderRadius: '14px', fontSize: '11px',
  background: 'var(--brio-gold-light, #fef9e7)',
  border: '1px solid var(--brio-gold, #c5a572)',
  color: 'var(--brio-text, #1e293b)',
};
const CC_CHIP_STYLE = {
  ...CHIP_STYLE,
  background: '#f0f4ff',
  border: '1px solid #93a8d0',
};
const BCC_CHIP_STYLE = {
  ...CHIP_STYLE,
  background: '#f5f0ff',
  border: '1px solid #a893d0',
};
const CHIP_REMOVE_STYLE = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: 0, lineHeight: 1, color: 'var(--brio-grey, #94a3b8)',
};

// ── Recipient chip row (reusable) ─────────────────────────────────────────
function RecipientChips({ recipients, chipStyle, onRemove, emptyText }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '6px',
      padding: '6px 8px', minHeight: '32px',
      border: '1px solid var(--brio-border, #d1d5db)', borderRadius: '6px',
      background: '#fff',
    }}>
      {recipients.length === 0 && (
        <span style={{ fontSize: '11px', color: 'var(--brio-muted, #94a3b8)', fontStyle: 'italic' }}>
          {emptyText || 'None'}
        </span>
      )}
      {recipients.map((r, i) => (
        <span key={i} style={chipStyle}>
          {r.name ? `${r.name} <${r.email}>` : r.email}
          <button style={CHIP_REMOVE_STYLE} onClick={() => onRemove(i)} title="Remove">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

/**
 * EmailDraftModal
 *
 * @param {object} props
 * @param {Function} props.onClose       - Close the modal (cancel)
 * @param {Function} props.onSaveOnly    - Save the timeline change without emailing
 * @param {Function} props.onSaveAndMail - Save the timeline change then open mailto
 * @param {Function} [props.onLogEmail]  - Optional callback to log the sent email
 * @param {object}   props.client        - Client record (from useSupabaseClients)
 * @param {object}   props.project       - Project record
 * @param {string}   props.stageName     - Human-readable stage name
 * @param {string}   props.eventType     - 'stage_initiated' | 'stage_approved' | 'invoice_sent' | etc.
 * @param {string}   props.dateValue     - The date being set (YYYY-MM-DD)
 * @param {object}   [props.invoice]     - Invoice record (for invoice-related templates)
 * @param {boolean}  [props.hasPdfAttachment] - Whether an invoice PDF is available
 * @param {string}   [props.pdfFilename] - Filename of attached PDF
 * @param {Array}    [props.stages]      - Ordered stage list for timeline block
 * @param {Array}    [props.teamMembers] - Team members for {teamList}
 * @param {object}   [props.extraVars]   - Additional template variables
 */
export default function EmailDraftModal({
  onClose,
  onSaveOnly,
  onSaveAndMail,
  onLogEmail,
  client,
  project,
  stageName,
  eventType,
  dateValue,
  customTemplates,
  invoice,
  hasPdfAttachment,
  pdfFilename,
  stages,
  teamMembers,
  extraVars,
}) {
  // ── Default addressee from primary contact name ─────────────────────
  const defaultAddressee = client?.name || client?.organisation || '';

  // ── Build full template vars with all dynamic placeholders ──────────
  const templateVars = useMemo(() => {
    const vars = buildTemplateVars({
      client,
      project,
      stageName,
      dateValue,
      invoice,
      stages,
      teamMembers,
      addressee: defaultAddressee,
      ...extraVars,
    });
    return vars;
  }, [client, project, stageName, dateValue, invoice, stages, teamMembers, defaultAddressee, extraVars]);

  // ── Initial template (supports admin-editable custom templates) ─────
  const initialTemplate = useMemo(
    () => renderEmailTemplate(eventType, templateVars, customTemplates),
    [eventType, templateVars, customTemplates]
  );

  // ── Suggested recipients (client-level contact prefs, project-level fallback) ─────
  const contactPrefs = client?.contactPreferences || project?.contactPreferences || {};
  const suggestedRecipients = useMemo(
    () => getClientContactsForEvent(client, eventType, contactPrefs),
    [client, eventType, contactPrefs] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const allContacts = useMemo(
    () => getAllClientContacts(client),
    [client]
  );

  // ── Local state ───────────────────────────────────────────────────────
  // Auto-BCC admin for finance events (invoice_sent, reminders)
  const initialBcc = useMemo(() => {
    if (isFinanceEvent(eventType)) {
      return [{ name: 'Admin', email: getAdminBccEmail() }];
    }
    return [];
  }, [eventType]);

  const [recipients, setRecipients] = useState(suggestedRecipients);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [bccRecipients, setBccRecipients] = useState(initialBcc);
  const [subject, setSubject] = useState(initialTemplate.subject);
  const [body, setBody] = useState(initialTemplate.body);
  // Addressee field — required, used in greeting "Dear {addressee},"
  const [addressee, setAddressee] = useState(defaultAddressee);
  // Optional intro/closing message sections
  const [introMessage, setIntroMessage] = useState('');
  const [closingMessage, setClosingMessage] = useState('');
  const [showIntroClosing, setShowIntroClosing] = useState(false);
  // Validation errors
  const [validationErrors, setValidationErrors] = useState([]);
  // Auto-expand CC/BCC row if BCC is pre-populated
  const [showCcBccInit] = useState(initialBcc.length > 0);
  const [manualEmail, setManualEmail] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(showCcBccInit);
  const [addTarget, setAddTarget] = useState('to'); // 'to' | 'cc' | 'bcc'

  // ── Re-render body when addressee or intro/closing changes ──────────
  const recomposeBody = useCallback((newAddressee, newIntro, newClosing) => {
    const vars = { ...templateVars, addressee: newAddressee || defaultAddressee };
    const rendered = renderEmailTemplate(eventType, vars, customTemplates, {
      introMessage: newIntro,
      closingMessage: newClosing,
    });
    setBody(rendered.body);
    setSubject(rendered.subject);
  }, [templateVars, defaultAddressee, eventType, customTemplates]);

  const handleAddresseeChange = (val) => {
    setAddressee(val);
    recomposeBody(val, introMessage, closingMessage);
  };

  const handleIntroChange = (val) => {
    setIntroMessage(val);
    recomposeBody(addressee, val, closingMessage);
  };

  const handleClosingChange = (val) => {
    setClosingMessage(val);
    recomposeBody(addressee, introMessage, val);
  };

  const handleResetToTemplate = () => {
    setAddressee(defaultAddressee);
    setIntroMessage('');
    setClosingMessage('');
    const vars = { ...templateVars, addressee: defaultAddressee };
    const rendered = renderEmailTemplate(eventType, vars, customTemplates);
    setSubject(rendered.subject);
    setBody(rendered.body);
    setValidationErrors([]);
  };

  // ── Handlers ──────────────────────────────────────────────────────────
  const removeFrom = (setter) => (index) => setter(prev => prev.filter((_, i) => i !== index));

  const allSelected = [...recipients, ...ccRecipients, ...bccRecipients];

  const addRecipient = (contact, target) => {
    const t = target || addTarget;
    const email = contact.email.toLowerCase();
    if (allSelected.some(r => r.email.toLowerCase() === email)) return;
    const entry = { name: contact.name, email: contact.email };
    if (t === 'cc') setCcRecipients(prev => [...prev, entry]);
    else if (t === 'bcc') setBccRecipients(prev => [...prev, entry]);
    else setRecipients(prev => [...prev, entry]);
  };

  const addManualRecipient = () => {
    const email = manualEmail.trim();
    if (!isValidEmail(email)) return;
    if (allSelected.some(r => r.email.toLowerCase() === email.toLowerCase())) return;
    const entry = { name: '', email };
    if (addTarget === 'cc') setCcRecipients(prev => [...prev, entry]);
    else if (addTarget === 'bcc') setBccRecipients(prev => [...prev, entry]);
    else setRecipients(prev => [...prev, entry]);
    setManualEmail('');
  };

  const handleSaveAndMail = () => {
    // Validate before opening mail
    const errors = validateEmailDraft({
      recipients,
      addressee,
      eventType,
      hasPdfAttachment: hasPdfAttachment || false,
    });
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);

    const validEmails = recipients.filter(r => isValidEmail(r.email)).map(r => r.email);
    const ccEmails = ccRecipients.filter(r => isValidEmail(r.email)).map(r => r.email);
    const bccEmails = bccRecipients.filter(r => isValidEmail(r.email)).map(r => r.email);
    const mailtoUrl = buildMailtoUrl({ to: validEmails, cc: ccEmails, bcc: bccEmails, subject, body });

    // Log the email event (non-blocking)
    if (onLogEmail) {
      try {
        onLogEmail({
          to: validEmails,
          cc: ccEmails,
          bcc: bccEmails,
          subject,
          eventType,
          stageName,
          dateValue,
        });
      } catch (_) { /* non-fatal */ }
    }

    // Call the save callback first
    if (onSaveAndMail) onSaveAndMail();

    // Open default mail client via location.href — more reliable than window.open
    // for mailto: links across browsers and platforms.
    if (mailtoUrl) {
      setTimeout(() => { window.location.href = mailtoUrl; }, 100);
    }
  };

  // ── Available contacts not yet selected ───────────────────────────────
  const availableContacts = allContacts.filter(
    c => !allSelected.some(r => r.email.toLowerCase() === c.email.toLowerCase())
  );

  const hasValidRecipients = recipients.some(r => isValidEmail(r.email));

  // ── Event type label ──────────────────────────────────────────────────
  const eventLabel = {
    stage_initiated: 'Stage Initiation',
    stage_completed: 'Stage Completed',
    stage_approved: 'Stage Approved',
    stage_closure: 'Stage Closure',
    timeline_change: 'Timeline Change',
    invoice_sent: 'Tax Invoice',
    reminder_friendly: 'Friendly Reminder',
    reminder_due_today: 'Due Today',
    reminder_overdue: 'Overdue Reminder',
    reminder_final: 'Final Reminder',
    monthly_accountant_report: 'Accountant Report',
  }[eventType] || eventType;

  return (
    <BrioModal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Mail className="w-4 h-4" style={{ color: 'var(--brio-gold, #c5a572)' }} />
          Email Draft — {eventLabel}
        </span>
      }
      onClose={onClose}
      width="lg"
      footer={
        <BrioActionGroup>
          <button className="brio-btn brio-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          {onSaveOnly && (
            <button className="brio-btn brio-btn-secondary" onClick={onSaveOnly}>
              Save without email
            </button>
          )}
          <button
            className="brio-btn brio-btn-primary"
            onClick={handleSaveAndMail}
            disabled={!hasValidRecipients}
            title={!hasValidRecipients ? 'Add at least one valid email recipient' : 'Opens draft in your mail app'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Mail className="w-3.5 h-3.5" />
            Open in Mail App
          </button>
        </BrioActionGroup>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '4px 0' }}>
        {/* Context banner */}
        <div style={{
          padding: '10px 14px', background: 'var(--brio-gold-light, #fef9e7)',
          border: '1px solid var(--brio-gold, #c5a572)', borderRadius: '6px',
          fontSize: '12px', color: 'var(--brio-text, #475569)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <strong>{project?.name}</strong> — {stageName} — {formatDateForEmail(dateValue)}
              {client && (
                <span style={{ marginLeft: '8px', color: 'var(--brio-grey, #94a3b8)' }}>
                  Client: {client.organisation || client.name}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleResetToTemplate}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                fontSize: '10px', color: 'var(--brio-muted, #94a3b8)',
                padding: '2px 4px', borderRadius: '3px',
              }}
              title="Reset subject and body to template defaults"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
          </div>
          <div style={{ marginTop: '4px', fontSize: '10px', color: 'var(--brio-muted, #94a3b8)' }}>
            Based on template: <strong>{eventLabel}</strong>
            {isFinanceEvent(eventType) && (
              <span style={{ marginLeft: '8px', color: '#9F902A' }}>Finance — admin BCC auto-included</span>
            )}
          </div>
        </div>

        {/* ── No contacts warning ── */}
        {allContacts.length === 0 && (
          <div style={{
            padding: '8px 12px', background: '#fffbeb',
            border: '1px solid #fbbf24', borderRadius: '6px',
            fontSize: '11px', color: '#92400e',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <AlertTriangle className="w-3 h-3" style={{ flexShrink: 0 }} />
            No contacts found for this client. Use the custom email field below to add recipients manually.
          </div>
        )}

        {/* ── To ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <label style={{ ...LABEL_STYLE, marginBottom: 0 }}>To</label>
            {!showCcBcc && (
              <button
                type="button"
                onClick={() => setShowCcBcc(true)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '11px', color: 'var(--brio-gold, #9F902A)', fontWeight: 500,
                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                }}
              >
                CC / BCC <ChevronDown className="w-3 h-3" />
              </button>
            )}
          </div>
          <RecipientChips
            recipients={recipients}
            chipStyle={CHIP_STYLE}
            onRemove={removeFrom(setRecipients)}
            emptyText="No recipients selected. Add contacts below or save without email."
          />
        </div>

        {/* ── CC / BCC (collapsible) ── */}
        {showCcBcc && (
          <>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <label style={{ ...LABEL_STYLE, marginBottom: 0 }}>CC</label>
                <button
                  type="button"
                  onClick={() => { setShowCcBcc(false); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '11px', color: 'var(--brio-muted, #94a3b8)',
                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                  }}
                >
                  Hide CC/BCC <ChevronUp className="w-3 h-3" />
                </button>
              </div>
              <RecipientChips
                recipients={ccRecipients}
                chipStyle={CC_CHIP_STYLE}
                onRemove={removeFrom(setCcRecipients)}
                emptyText="No CC recipients"
              />
            </div>
            <div>
              <label style={LABEL_STYLE}>BCC</label>
              <RecipientChips
                recipients={bccRecipients}
                chipStyle={BCC_CHIP_STYLE}
                onRemove={removeFrom(setBccRecipients)}
                emptyText="No BCC recipients"
              />
            </div>
          </>
        )}

        {/* ── Add contacts (shared controls for To/CC/BCC) ── */}
        <div>
          {/* Target selector (when CC/BCC visible) */}
          {showCcBcc && (
            <div style={{ display: 'flex', gap: '2px', marginBottom: '6px' }}>
              {['to', 'cc', 'bcc'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setAddTarget(t)}
                  style={{
                    padding: '2px 10px', fontSize: '10px', fontWeight: 600,
                    border: '1px solid var(--brio-border, #d1d5db)',
                    borderRadius: t === 'to' ? '4px 0 0 4px' : t === 'bcc' ? '0 4px 4px 0' : '0',
                    background: addTarget === t ? 'var(--brio-gold, #c5a572)' : '#fff',
                    color: addTarget === t ? '#fff' : 'var(--brio-text, #475569)',
                    cursor: 'pointer',
                    ...(t !== 'to' ? { borderLeft: 'none' } : {}),
                  }}
                >
                  {t.toUpperCase()}
                </button>
              ))}
              <span style={{ fontSize: '10px', color: 'var(--brio-muted, #94a3b8)', marginLeft: '6px', alignSelf: 'center' }}>
                Adding to: {addTarget.toUpperCase()}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            {availableContacts.map((c, i) => (
              <button
                key={i}
                className="brio-btn brio-btn-secondary"
                style={{ padding: '2px 8px', fontSize: '10px' }}
                onClick={() => addRecipient(c)}
                title={`Add ${c.email} to ${addTarget.toUpperCase()}`}
              >
                + {c.name || c.email}{c.roleLabel ? ` (${c.roleLabel})` : ''}
              </button>
            ))}

            <button
              className="brio-btn brio-btn-secondary"
              style={{ padding: '2px 8px', fontSize: '10px' }}
              onClick={() => setShowAddContact(!showAddContact)}
              title="Add custom email"
            >
              <UserPlus className="w-3 h-3" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />
              Custom
            </button>
          </div>

          {showAddContact && (
            <div style={{ marginTop: '6px', display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="email"
                placeholder="email@example.com"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManualRecipient(); } }}
                style={{ ...INPUT_STYLE, flex: 1, maxWidth: '280px' }}
              />
              <button
                className="brio-btn brio-btn-primary"
                style={{ padding: '4px 12px', fontSize: '11px' }}
                onClick={addManualRecipient}
                disabled={!isValidEmail(manualEmail.trim())}
              >
                Add to {addTarget.toUpperCase()}
              </button>
            </div>
          )}
        </div>

        {/* ── Validation errors ── */}
        {validationErrors.length > 0 && (
          <div style={{
            padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5',
            borderRadius: '6px', fontSize: '11px', color: '#991b1b',
          }}>
            {validationErrors.map((err, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: i < validationErrors.length - 1 ? '4px' : 0 }}>
                <AlertTriangle className="w-3 h-3" style={{ flexShrink: 0 }} />
                {err.message}
              </div>
            ))}
          </div>
        )}

        {/* ── Addressee (required) ── */}
        <div>
          <label style={LABEL_STYLE}>
            Addressee <span style={{ color: 'var(--brio-red, #dc2626)', fontWeight: 400, fontSize: '10px' }}>required</span>
          </label>
          <input
            type="text"
            value={addressee}
            onChange={(e) => handleAddresseeChange(e.target.value)}
            placeholder="e.g. Mr. Sharma, Ms. van den Berg"
            style={{
              ...INPUT_STYLE,
              borderColor: !addressee.trim() ? 'var(--brio-red, #dc2626)' : INPUT_STYLE.border?.split(' ').pop() || '#d1d5db',
              background: !addressee.trim() ? '#fef2f2' : '#fff',
            }}
          />
          {!addressee.trim() && (
            <span style={{ fontSize: '10px', color: 'var(--brio-red, #dc2626)', marginTop: '2px', display: 'block' }}>
              Addressee is required — used in greeting "Dear …,"
            </span>
          )}
        </div>

        {/* Subject */}
        <div>
          <label style={LABEL_STYLE}>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={INPUT_STYLE}
          />
        </div>

        {/* ── Intro / Closing sections (collapsible) ── */}
        <div>
          <button
            type="button"
            onClick={() => setShowIntroClosing(!showIntroClosing)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '11px', color: 'var(--brio-gold, #9F902A)', fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: '3px', padding: 0,
            }}
          >
            {showIntroClosing ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Intro &amp; Closing messages
          </button>

          {showIntroClosing && (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={{ ...LABEL_STYLE, fontWeight: 500 }}>
                  Intro message <span style={{ fontWeight: 400, color: 'var(--brio-muted, #94a3b8)' }}>(optional — appears after greeting)</span>
                </label>
                <textarea
                  value={introMessage}
                  onChange={(e) => handleIntroChange(e.target.value)}
                  placeholder="Add a personal intro before the template body…"
                  style={{ ...INPUT_STYLE, minHeight: '60px', resize: 'vertical', fontFamily: "'Raleway', sans-serif" }}
                />
              </div>
              <div>
                <label style={{ ...LABEL_STYLE, fontWeight: 500 }}>
                  Closing message <span style={{ fontWeight: 400, color: 'var(--brio-muted, #94a3b8)' }}>(optional — appears before sign-off)</span>
                </label>
                <textarea
                  value={closingMessage}
                  onChange={(e) => handleClosingChange(e.target.value)}
                  placeholder="Add a personal note before the sign-off…"
                  style={{ ...INPUT_STYLE, minHeight: '60px', resize: 'vertical', fontFamily: "'Raleway', sans-serif" }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Body (fully editable) */}
        <div>
          <label style={LABEL_STYLE}>Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            style={TEXTAREA_STYLE}
          />
        </div>

        {/* ── Attachment indicator ── */}
        {requiresInvoicePdf(eventType) && (
          <div style={{
            padding: '8px 12px',
            background: hasPdfAttachment ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${hasPdfAttachment ? '#86efac' : '#fca5a5'}`,
            borderRadius: '6px', fontSize: '11px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <FileText className="w-3 h-3" style={{ color: hasPdfAttachment ? '#16a34a' : '#dc2626', flexShrink: 0 }} />
            {hasPdfAttachment ? (
              <span>PDF attached: <strong>{pdfFilename || 'invoice.pdf'}</strong> — remember to attach manually in your mail app</span>
            ) : (
              <span style={{ color: '#991b1b' }}>Invoice PDF not yet generated — required for this email type</span>
            )}
          </div>
        )}
      </div>
    </BrioModal>
  );
}
